import { useCallback, useEffect, useRef, useState } from "react";

import {
  getAssistantWalkthroughFeatures,
  acknowledgeAssistantWalkthroughFeature,
  updateAssistantWalkthroughScenario,
} from "api/services/chat/assistant";
import { NotificationMode, showSnackBar, store } from "store";

import { AssistantScenarioStatusEnum } from "../enums";

import {
  IUseAssistantWalkthroughFeaturesOptions,
  IAssistantWalkthroughFeatureState,
  IUseAssistantWalkthroughFeaturesReturn,
} from "../types";

export function useAssistantWalkthroughFeatures({
  identity,
  enabled,
  open,
}: IUseAssistantWalkthroughFeaturesOptions): IUseAssistantWalkthroughFeaturesReturn {
  const [state, setState] = useState<IAssistantWalkthroughFeatureState>({
    identity,
    walkthroughFeatures: [],
  });
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const activeIdentity = useRef<string>();
  const generation = useRef(0);
  const request = useRef(0);
  const inFlight = useRef(new Set<string>());

  const handleRefresh = useCallback(async () => {
    if (!enabled || activeIdentity.current !== identity) return;
    const context = generation.current;
    const currentRequest = ++request.current;

    try {
      const result = await getAssistantWalkthroughFeatures();
      if (
        context === generation.current &&
        currentRequest === request.current
      ) {
        setState({ identity, walkthroughFeatures: result });
      }
    } catch {
      // The shared Apollo request layer owns error feedback.
    }
  }, [enabled, identity]);

  useEffect(() => {
    activeIdentity.current = identity;
    generation.current += 1;
    request.current += 1;
    inFlight.current.clear();
    setState({ identity, walkthroughFeatures: [] });
    setPendingIds([]);
    void handleRefresh();

    const handleVisible = () => {
      if (document.visibilityState === "visible") void handleRefresh();
    };

    window.addEventListener("focus", handleVisible);
    document.addEventListener("visibilitychange", handleVisible);

    return () => {
      activeIdentity.current = undefined;
      generation.current += 1;
      request.current += 1;
      window.removeEventListener("focus", handleVisible);
      document.removeEventListener("visibilitychange", handleVisible);
    };
  }, [identity, handleRefresh]);

  useEffect(() => {
    if (open) void handleRefresh();
  }, [open, handleRefresh]);

  const handleAcknowledge = useCallback(
    async (walkthroughFeatureId: string) => {
      if (!enabled || inFlight.current.has(walkthroughFeatureId)) return false;
      const context = generation.current;
      inFlight.current.add(walkthroughFeatureId);
      request.current += 1;
      setPendingIds((ids) => [...ids, walkthroughFeatureId]);

      try {
        const result = await acknowledgeAssistantWalkthroughFeature({
          walkthroughFeatureId,
        });
        if (context !== generation.current) return false;
        if (result === true) {
          request.current += 1;
          setState((current) =>
            current.identity === identity
              ? {
                  ...current,
                  walkthroughFeatures: current.walkthroughFeatures.filter(
                    (item) => item.id !== walkthroughFeatureId
                  ),
                }
              : current
          );
          return true;
        } else {
          store.dispatch(
            showSnackBar({
              message: "The teaching offer was not saved.",
              mode: NotificationMode.ERROR,
            })
          );
        }
      } catch {
        // The shared Apollo request layer owns error feedback.
      } finally {
        if (context === generation.current) {
          inFlight.current.delete(walkthroughFeatureId);
          setPendingIds((ids) =>
            ids.filter((id) => id !== walkthroughFeatureId)
          );
        }
      }
      return false;
    },
    [enabled, identity]
  );

  const handleCompleteGuide = useCallback(
    async (guideId: string) => {
      if (!enabled) return false;
      const context = generation.current;

      try {
        const currentWalkthroughFeatures =
          await getAssistantWalkthroughFeatures();
        if (context !== generation.current) return false;
        const results = await Promise.all(
          currentWalkthroughFeatures
            .filter((feature) =>
              feature.scenarios.some(
                (scenario) => scenario.guide.id === guideId
              )
            )
            .map((feature) =>
              updateAssistantWalkthroughScenario({
                walkthroughFeatureId: feature.id,
                guideId,
                status: AssistantScenarioStatusEnum.COMPLETED,
              })
            )
        );
        if (context !== generation.current) return false;
        if (results.some((result) => result !== true)) {
          store.dispatch(
            showSnackBar({
              message: "Scenario progress was not saved.",
              mode: NotificationMode.ERROR,
            })
          );
          return false;
        }
        void handleRefresh();
        return true;
      } catch {
        // The shared Apollo request layer owns error feedback.
        return false;
      }
    },
    [enabled, handleRefresh]
  );

  const isCurrentIdentity = enabled && state.identity === identity;

  return {
    walkthroughFeatures: isCurrentIdentity ? state.walkthroughFeatures : [],
    pendingIds: isCurrentIdentity ? pendingIds : [],
    handleAcknowledge,
    handleCompleteGuide,
    handleRefresh,
  };
}
